import React, { useMemo, useRef } from "react";
import { Box, Button, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";

const DEFAULT_PRESET_COLORS = ["#00C853", "#FF6D00", "#FFAB00", "#42A5F5", "#9C27B0", "#E91E63"];

const PRESET_SWATCH_SIZE = 26;
const SELECTED_SWATCH_WIDTH = 40;
const SELECTED_SWATCH_HEIGHT = 30;
const SWATCH_RADIUS = 6;

function normalizeHexColor(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const upper = raw.startsWith("#") ? raw.toUpperCase() : `#${raw.toUpperCase()}`;
  return /^#([0-9A-F]{6})$/.test(upper) ? upper : upper;
}

function swatchStyle({
  background,
  border,
  marginRight = 8,
  marginBottom = 6,
  width = PRESET_SWATCH_SIZE,
  height = PRESET_SWATCH_SIZE,
}) {
  return {
    width,
    height,
    borderRadius: SWATCH_RADIUS,
    marginRight,
    marginBottom,
    background,
    border,
    cursor: "pointer",
    padding: 0,
    flexShrink: 0,
  };
}

export default function ColorPicker({
  label,
  value,
  onChange,
  onCancel,
  onOk,
  required,
  presetColors = DEFAULT_PRESET_COLORS,
  dark = false,
}) {
  const { t } = useTranslation();
  const colorInputRef = useRef(null);
  const normalized = normalizeHexColor(value);
  const isValid = useMemo(() => /^#([0-9A-F]{6})$/.test(normalized), [normalized]);

  const textColor = dark ? "#F5F5F5" : "#121212";
  const borderColor = dark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.16)";
  const displayColor = isValid ? normalized : "#000000";

  return (
    <Box>
      {label && (
        <Typography
          variant="body2"
          style={{ color: textColor, fontSize: 10, marginTop: 8 }}
        >
          {t(label)}
          {required ? " *" : ""}
        </Typography>
      )}

      <Box display="flex" alignItems="center">
        <input
          ref={colorInputRef}
          type="color"
          value={displayColor}
          onChange={(e) => onChange?.(normalizeHexColor(e.target.value))}
          aria-label={label ? t(label) : "Color"}
          style={{
            position: "absolute",
            width: 1,
            height: 1,
            opacity: 0,
            overflow: "hidden",
            clip: "rect(0,0,0,0)",
            whiteSpace: "nowrap",
            border: 0,
          }}
          tabIndex={-1}
        />
        <button
          type="button"
          onClick={() => colorInputRef.current?.click()}
          aria-label={label ? `${t(label)} custom` : "Custom color"}
          style={swatchStyle({
            background: displayColor,
            border: "none",
            marginRight: 8,
            width: SELECTED_SWATCH_WIDTH,
            height: SELECTED_SWATCH_HEIGHT,
          })}
        />
        {(presetColors || []).map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => onChange?.(normalizeHexColor(c))}
            aria-label={c}
            style={swatchStyle({
              background: c,
              border:
                c.toUpperCase() === normalized
                  ? `2px solid ${textColor}`
                  : `1px solid ${borderColor}`,
            })}
          />
        ))}
      </Box>

      {(onOk || onCancel) && (
        <Box mt={1}>
          {onOk && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              style={{ marginRight: 8, background: "#6F9CEB", color: "#fff", fontWeight: 600 }}
              onClick={() => onOk?.(normalized)}
              disabled={!isValid}
            >
              {t("OK")}
            </Button>
          )}
          {onCancel && (
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              style={dark ? { color: "#fff", borderColor: "#fff" } : undefined}
              onClick={() => onCancel?.()}
            >
              {t("Cancel")}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
